<script setup lang="ts">
import { LayoutRowTwoFocusBottomIcon, TableCalculatorIcon } from '@blpde/nx-icons';
import { computed, watch } from 'vue';
import { NxRadioCardItem, NxRadioGroup } from './../Inputs';
import { useNxPaneState } from '../Settings/settingsSource';

export type NxLayoutMode = 'cards' | 'table';

const props = withDefaults(
  defineProps<{
    /**
     * When set, the selected mode is remembered in the user's settings
     * (`shell.panes.<persistPrefix>.<persistKey>`, the kit's settings
     * contract, I-2510).
     */
    persistKey: string;
    /** Defaults to `'nx.layout'`. */
    persistPrefix?: string;
    /** Matches NxButton height tokens. Defaults to `'s'`. */
    height?: 'xs' | 's' | 'm' | 'l' | 'xl';
    cardsLabel?: string;
    tableLabel?: string;
  }>(),
  {
    persistPrefix: 'nx.layout',
    height: 's',
    cardsLabel: 'Karten',
    tableLabel: 'Tabelle',
  }
);
const modelValue = defineModel<NxLayoutMode>({ required: true });

const pane = useNxPaneState<{ mode: NxLayoutMode }>(() =>
  props.persistKey ? `${props.persistPrefix}.${props.persistKey}` : ''
);

function isLayoutMode(value: unknown): value is NxLayoutMode {
  return value === 'cards' || value === 'table';
}

const stored = computed<NxLayoutMode | null>(() => {
  const mode = pane.state.value.mode;
  return isLayoutMode(mode) ? mode : null;
});

// A remembered mode wins over the start value, also when it arrives later.
watch(
  stored,
  (mode) => {
    if (mode && mode !== modelValue.value) modelValue.value = mode;
  },
  { immediate: true }
);

watch(
  () => modelValue.value,
  (value) => {
    if (value !== stored.value) pane.patch({ mode: value });
  }
);
</script>

<template>
  <!-- The value is the mode itself: „card“ never matched `cards`, so the cards
       segment was never shown as selected and a persisted pick was dropped. -->
  <NxRadioGroup v-model="modelValue" :gap="null">
    <NxRadioCardItem value="cards" icon-only :height="props.height" :label="props.cardsLabel">
      <LayoutRowTwoFocusBottomIcon class="nxt-icon-md" />
    </NxRadioCardItem>
    <NxRadioCardItem value="table" icon-only :height="props.height" :label="props.tableLabel">
      <TableCalculatorIcon class="nxt-icon-md" />
    </NxRadioCardItem>
  </NxRadioGroup>
</template>
